import type { ReactNode, RefObject } from "react"
import { useEffect } from "react"
import { useLatest } from "react-use"

import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { Shortcut } from "@productlane/ui"

import type { MoreOptionField } from "./create-issue-more-options-fields"

export type CreateIssuePicker =
	| "status"
	| "priority"
	| "assignee"
	| "project"
	| "labels"
	| MoreOptionField

/** A picker to open, or Linear's I: assign the issue to yourself. */
export type CreateIssueShortcut = CreateIssuePicker | "assignSelf"

interface ShortcutKeys {
	key: string
	shift?: boolean
	alt?: boolean
	ctrl?: boolean
}

// Linear's own bindings, so muscle memory carries over.
export const CREATE_ISSUE_SHORTCUTS: Partial<
	Record<CreateIssueShortcut, ShortcutKeys>
> = {
	status: { key: "s" },
	assignSelf: { key: "i" },
	priority: { key: "p" },
	assignee: { key: "a" },
	labels: { key: "l" },
	project: { key: "p", shift: true },
	dueDate: { key: "d", shift: true },
	cycle: { key: "c", shift: true },
	estimate: { key: "e", shift: true },
	milestone: { key: "m", shift: true },
	link: { key: "l", ctrl: true },
	release: { key: "r", alt: true },
}

// These take typed input or search as they open, so there is always something to do.
const ALWAYS_OPENABLE: ReadonlySet<CreateIssuePicker> = new Set([
	"dueDate",
	"link",
	"parent",
])

/** A picker still loading, or with nothing in it, stays shut. */
export function canOpenPicker(
	picker: CreateIssuePicker,
	choiceCounts: Partial<Record<CreateIssuePicker, number>>,
) {
	return ALWAYS_OPENABLE.has(picker) || (choiceCounts[picker] ?? 0) > 0
}

/** The Linear member behind the signed-in user, matched on email. */
export function findSelfMember<T extends { email: string }>(
	members: T[] | null | undefined,
	email: string | undefined,
) {
	const normalized = email?.trim().toLowerCase()
	if (!normalized) return undefined
	return members?.find((member) => member.email.toLowerCase() === normalized)
}

type ShortcutEvent = Pick<
	KeyboardEvent,
	"key" | "code" | "shiftKey" | "altKey" | "ctrlKey" | "metaKey"
>

function eventLetter(event: ShortcutEvent) {
	const fromCode = /^Key([A-Z])$/.exec(event.code)?.[1]?.toLowerCase()
	// ⌥ rewrites the key on macOS (⌥R reads "®"), so use the physical key there.
	if (event.altKey) return fromCode
	return event.key?.length === 1 ? event.key.toLowerCase() : fromCode
}

export function matchCreateIssueShortcut(
	event: ShortcutEvent,
): CreateIssueShortcut | null {
	if (event.metaKey) return null
	const letter = eventLetter(event)
	if (!letter) return null
	for (const [picker, keys] of Object.entries(CREATE_ISSUE_SHORTCUTS)) {
		if (
			keys.key === letter &&
			!!keys.shift === event.shiftKey &&
			!!keys.alt === event.altKey &&
			!!keys.ctrl === event.ctrlKey
		) {
			return picker as CreateIssueShortcut
		}
	}
	return null
}

const TEXT_ENTRY =
	'input, textarea, select, [contenteditable]:not([contenteditable="false"])'

export function isCreateIssueShortcutTarget(
	event: Pick<KeyboardEvent, "target" | "ctrlKey" | "metaKey">,
	container: HTMLElement | null,
) {
	const target = event.target
	if (!container || !(target instanceof Element)) return false
	// Focus drops to the body after a picker closes; the next key still belongs to the dialog.
	if (!container.contains(target) && target !== target.ownerDocument.body) {
		return false
	}
	if (event.ctrlKey || event.metaKey) return true
	return !target.closest(TEXT_ENTRY)
}

export function useCreateIssueShortcuts(
	enabled: boolean,
	containerRef: RefObject<HTMLElement | null>,
	onShortcut: (shortcut: CreateIssueShortcut) => void,
) {
	const onShortcutRef = useLatest(onShortcut)

	useEffect(() => {
		if (!enabled) return
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.defaultPrevented || event.repeat || event.isComposing) return
			const picker = matchCreateIssueShortcut(event)
			if (!picker || !isCreateIssueShortcutTarget(event, containerRef.current))
				return
			event.preventDefault()
			event.stopPropagation()
			onShortcutRef.current(picker)
		}
		// Not useShortcut: the dialog's Radix focus guards make it drop bare keys, and
		// popover triggers stop keydown from bubbling, so listen in the capture phase.
		window.addEventListener("keydown", onKeyDown, true)
		return () => window.removeEventListener("keydown", onKeyDown, true)
	}, [enabled, containerRef, onShortcutRef])
}

export function createIssueShortcutKeys(picker: CreateIssuePicker): string[] {
	const keys = CREATE_ISSUE_SHORTCUTS[picker]
	if (!keys) return []
	return [
		...(keys.ctrl ? [ShortcutSymbols.CTRL] : []),
		...(keys.alt ? [ShortcutSymbols.ALT] : []),
		...(keys.shift ? [ShortcutSymbols.SHIFT] : []),
		keys.key.toUpperCase(),
	]
}

/** "⇧D", "Ctrl L", "⌥R": the trailing hint in a menu row, as Linear writes it. */
export function createIssueShortcutHint(picker: CreateIssuePicker) {
	const keys = createIssueShortcutKeys(picker)
	if (!keys.length) return undefined
	return keys
		.map((key) => (key === ShortcutSymbols.CTRL ? "Ctrl " : key))
		.join("")
}

export const ShortcutTooltipLabel = ({
	label,
	picker,
}: {
	label: ReactNode
	picker: CreateIssuePicker
}) => (
	<div className="flex items-center gap-2">
		{label}
		<span className="flex items-center gap-1">
			{createIssueShortcutKeys(picker).map((key) => (
				<Shortcut key={key}>{key}</Shortcut>
			))}
		</span>
	</div>
)
